<template>
  <div class="failed-container">
    <div class="failed-content">
      <div class="failed-icon">
        <div class="error-mark">✕</div>
      </div>

      <h1 class="failed-title">Payment Failed</h1>
      <p class="failed-message">{{ errorMessage }}</p>

      <div class="error-details" v-if="errorDetails">
        <div class="detail-card">
          <h3>Error Information</h3>
          <div class="detail-item">
            <span class="label">Error Code:</span>
            <span class="value">{{ errorCode }}</span>
          </div>
          <div class="detail-item" v-if="fawryStatus">
            <span class="label">Payment Status:</span>
            <span class="value">{{ fawryStatus }}</span>
          </div>
          <div class="detail-item" v-if="reference">
            <span class="label">Reference:</span>
            <span class="value">{{ reference }}</span>
          </div>
        </div>
      </div>

      <div class="failed-actions">
        <button @click="retryPayment" class="primary-button">
          Try Again
        </button>
        <button @click="goBack" class="secondary-button">
          Go Back
        </button>
      </div>

      <div class="help-section">
        <h3>Need Help?</h3>
        <p>If you continue to experience issues, please contact our support team with the error details above.</p>
        <div class="help-actions">
          <a href="https://quali-d.com/support" target="_blank" class="help-link">
            Contact Support
          </a>
          <a href="https://quali-d.com/faq" target="_blank" class="help-link">
            View FAQ
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaymentFailedView',
  data () {
    return {
      errorCode: 'unknown',
      fawryStatus: null,
      reference: null,
      errorDetails: true
    }
  },
  computed: {
    errorMessage () {
      const messages = {
        missing_reference: 'The payment reference is missing or invalid. Please try again.',
        invalid_payment: 'The payment could not be processed. Please check your payment details and try again.',
        system_error: 'A system error occurred while processing your payment. Please try again in a few minutes.',
        payment_declined: 'Your payment was declined. Please check your card details or try a different payment method.',
        expired: 'The payment session has expired. Please start the payment process again.',
        cancelled: 'The payment was cancelled. You can try again whenever you\'re ready.',
        timeout: 'The payment request timed out. Please try again.',
        insufficient_funds: 'Insufficient funds available. Please check your account balance or try a different payment method.',
        default: 'An error occurred while processing your payment. Please try again or contact support.'
      }

      return messages[this.errorCode] || messages.default
    }
  },
  mounted () {
    this.errorCode = this.$route.query.error || 'unknown'
    this.fawryStatus = this.$route.query.status || null
    this.reference = this.$route.query.ref || null

    // If we have a Fawry status, use it as error code for more specific messaging
    if (this.fawryStatus) {
      this.errorCode = this.fawryStatus.toLowerCase()
    }

    // Clear pending login credentials if payment failed
    localStorage.removeItem('pendingLoginCredentials')
  },
  methods: {
    retryPayment () {
      // Go back to the payments or register page depending on context
      const isRenewal = this.$route.query.type === 'renewal'
      if (isRenewal) {
        this.$router.push('/payments')
      } else {
        this.$router.push('/register')
      }
    },
    goBack () {
      // Use browser back or fallback to appropriate page
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        const isRenewal = this.$route.query.type === 'renewal'
        this.$router.push(isRenewal ? '/payments' : '/register')
      }
    }
  }
}
</script>

<style scoped>
.failed-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.failed-content {
  background: white;
  border-radius: 20px;
  padding: 48px;
  max-width: 600px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
}

.failed-icon {
  margin-bottom: 32px;
}

.error-mark {
  width: 100px;
  height: 100px;
  background: #ef4444;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: white;
  font-weight: bold;
  animation: scaleIn 0.5s ease-out;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

.failed-title {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
}

.failed-message {
  font-size: 18px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 32px 0;
}

.error-details {
  margin-bottom: 32px;
}

.detail-card {
  background: #fef2f2;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #fecaca;
}

.detail-card h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #fecaca;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item .label {
  font-weight: 500;
  color: #6b7280;
}

.detail-item .value {
  font-weight: 600;
  color: #1f2937;
  font-family: monospace;
}

.failed-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 32px;
}

.primary-button {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
}

.primary-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
}

.secondary-button {
  background: white;
  color: #6b7280;
  border: 2px solid #e5e7eb;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
}

.secondary-button:hover {
  border-color: #d1d5db;
  color: #374151;
}

.help-section {
  padding-top: 32px;
  border-top: 1px solid #e5e7eb;
}

.help-section h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.help-section p {
  color: #6b7280;
  margin: 0 0 16px 0;
  font-size: 14px;
}

.help-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.help-link {
  color: #3b82f6;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s;
}

.help-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .failed-content {
    padding: 32px 24px;
    margin: 16px;
  }

  .failed-title {
    font-size: 24px;
  }

  .failed-message {
    font-size: 16px;
  }

  .error-mark {
    width: 80px;
    height: 80px;
    font-size: 36px;
  }

  .failed-actions {
    flex-direction: column;
  }

  .primary-button,
  .secondary-button {
    width: 100%;
  }

  .help-actions {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
